import { IconValue } from "@cmp-src/icons";
import * as mdiIcons from "@mdi/js";
import React, { HTMLProps } from "react";
import { LucideIcon, LucideIconName } from "../LucideIcon";
import * as TacticIcons from "../tactic-icons";

export interface IconFromSetProps {
  /**
   * The icon to draw: a value saved by the icon picker, `{ set, icon }`, or a component.
   *
   * - `{ set: "tactic", icon: "Coffee" }`: the Tactic space icons (see Foundations/Icons).
   * - `{ set: "lucide", icon: "Coffee" }`: any `lucide-react` icon, by name without the `Icon` suffix.
   * - `{ set: "mdi", icon: "mdiCoffee" }`: any Material Design icon from `@mdi/js`.
   *
   * Nothing renders for `null` or for an unknown set.
   */
  icon: IconValue | React.FC | null;
  /**
   * Width and height in 4px units, like Tailwind spacing: `8` is 32px, `6` is 24px.
   * @default 8
   */
  size?: number;
  /** Fill / stroke colour. Defaults to `currentColor`, so the icon follows the text colour. */
  color?: string;
  /** Inline styles for the SVG; `width` and `height` here win over `size`. */
  style?: React.SVGProps<SVGSVGElement>["style"];
  preserveAspectRatio?: React.SVGProps<SVGSVGElement>["preserveAspectRatio"];
}

/**
 * Draws an icon chosen by a user and stored as data, whatever set it comes from. Use it for
 * anything picked with `IconPicker` (resource types, amenities, request categories); for icons
 * chosen in code, import the icon component directly.
 */
export const IconFromSet: React.FC<IconFromSetProps> = ({
  icon,
  size = 8,
  color,
  style,
  preserveAspectRatio
}) => {
  if (!icon) return null;

  // A component rather than a stored { set, icon } value: plain function components
  // and forwardRef/memo objects alike
  if (typeof icon === "function" || !("set" in icon)) {
    const IconComponent = icon as React.FC<HTMLProps<HTMLDivElement>>;
    return (
      <IconComponent
        style={{
          width: size * 4,
          height: size * 4,
          ...style
        }}
      />
    );
  }

  if (typeof icon !== "object" && (!("set" in icon) || !("icon" in icon))) {
    return null;
  }

  const { icon: iconName, set } = icon;

  if (set === "mdi")
    return (
      // biome-ignore lint/a11y/noSvgWithoutTitle: <explanation>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={"0 0 24 24"}
        width={`${style?.width || size * 4}px`}
        height={`${style?.height || size * 4}px`}
        fill={"none"}
        strokeWidth={2}
        style={style}
        preserveAspectRatio={preserveAspectRatio}
      >
        <path
          d={mdiIcons[iconName as never]}
          style={{ fill: color || "currentColor" }}
        />
      </svg>
    );
  if (set === "lucide")
    return (
      <LucideIcon
        name={iconName as LucideIconName}
        size={size}
        color={color}
        style={style}
        preserveAspectRatio={"none"}
      />
    );
  if (set === "tactic" && (iconName as string) in TacticIcons) {
    const IconComponent = TacticIcons[iconName as keyof typeof TacticIcons];
    return (
      <IconComponent
        style={{
          width: size * 4,
          height: size * 4,
          color: color || "currentColor",
          fill: color || "currentColor",
          ...style
        }}
        width={style?.width || size * 4}
        height={style?.height || size * 4}
        preserveAspectRatio={preserveAspectRatio || "xMidYMid meet"}
      />
    );
  }
  //if (set === "custom") return <CustomIcon id={iconName} size={size * 4} />;
  return null;
};
